/**
 * Server refusals that deserve their own words.
 *
 * Both backends answer some conditions with a `code` and a human sentence
 * rather than a generic failure, because "please try again" is wrong or
 * unhelpful for all of them: an upload paused for a migration will work again
 * shortly, a file over the size limit never will until it is smaller, and a
 * rate-limited login code needs the user to wait rather than retry.
 *
 * Call sites keep their own fallback; this only replaces it when the server
 * actually said something better.
 */
export const REFUSAL_CODES = [
  'uploads_paused',      // keyon/lib/LegacyUploads.php, R1 image only (the cutover image no longer pauses)
  'payload_too_large',   // keyon/lib/PostSizeGuard.php
  'too_many_requests',   // keyon/lib/LoginCodeCaps.php, issuing; keyon/lib/TwilioAccess.php, texts and calls
  'too_many_attempts',   // keyon/lib/LoginCodeCaps.php, verifying
  'locked',              // keyon/lib/LoginCodeCaps.php
  'expired',             // keyon/lib/LoginCodeCaps.php
  'csrf',                // keyon/lib/CsrfGuard.php, pacmny-be/lib/CsrfFilter.php
  'invalid_attachment',  // keyon/lib/StoredFileWriter.php
  'unsupported_type',    // keyon/lib/StoredFileWriter.php
  'uploads_unavailable', // keyon/lib/StoredFileWriter.php
  'too_large',           // keyon/lib/StoredFileWriter.php
  'too_many_attachments', // keyon/lib/StoredFileWriter.php
  'invalid_file_uid',    // keyon/lib/StoredFileWriter.php
  'upload_error',        // keyon/lib/StoredFileWriter.php, keyon/lib/EmailAttachment.php, keyon/tswebhook.php: a file that did not arrive whole
  'rate_limited',        // keyon/tswebhook.php, verify-nurse re-uploads and finalize (file.php sends it with no sentence)
  'invite_required',     // keyon/lib/VerifyNurseAuth.php, keyon/lib/AuthorizationWrite.php
  // Every emitter that sends a sentence sends one a person can act on:
  // AuthorizationWrite::REFUSAL_MESSAGES (SessionActor and UploadRefusal reuse
  // it) and NursysAccess::MESSAGES (tswebhook nursysGuard). keyon/file.php
  // sends the code alone, which keeps the caller's own fallback.
  'unauthenticated',     // keyon/lib/AuthorizationWrite.php, keyon/lib/SessionActor.php, keyon/lib/NursysAccess.php
  'forbidden',           // keyon/lib/AuthorizationWrite.php, keyon/lib/SessionActor.php, keyon/lib/NursysAccess.php; keyon/lib/TwilioAccess.php: a text or call to a patient without privilege 6
  'actor_mismatch',      // keyon/lib/AuthorizationWrite.php, keyon/lib/SessionActor.php
  'reason_required',     // keyon/lib/UploadRefusal.php: DeleteDirectiveDocument, DeleteMessageAttachment
  'directive_finalized', // keyon/lib/UploadRefusal.php: DeleteDirectiveDocument on a signed or closed record
  'document_removed',    // keyon/lib/UploadRefusal.php: SignDirectiveRecord, ConfirmDirectiveAuthorization on an upload whose document was removed
  'recipient_not_on_file', // keyon/lib/TwilioAccess.php: SendText, SendConferenceText, SendPhoneCall to a number not on file for the account, or to a named patient with none
  'caller_not_on_file',  // keyon/lib/TwilioAccess.php: SendPhoneCall by someone with no number of their own on keyon's record
  'no_caller_number',    // keyon/lib/TwilioAccess.php: SendPhoneCall with no number to connect to, before enforcement (the profile phone is the remedy then)
  'invalid_number',      // keyon/lib/TwilioAccess.php: not one US number - an extension is dropped, anything else is refused
  'too_many_recipients', // keyon/lib/TwilioAccess.php
  'no_recipient',        // keyon/lib/TwilioAccess.php
  'sms_failed',          // keyon/lib/TwilioAccess.php: Twilio did not take the text (HTTP 502)
  'call_failed',         // keyon/lib/TwilioAccess.php: Twilio did not take the call (HTTP 502)
  // Video (sentences in keyon/lib/UploadRefusal.php). Only stream_busy is file.php's,
  // and file.php sends the code alone, so a page that shows it supplies the
  // sentence. The rest are keyon/video_upload.php's.
  'stream_busy',            // keyon/lib/StreamSlots.php, via file.php: a person with four ranged reads already open (HTTP 429)
  'unsupported_video',      // keyon/lib/VideoContainer.php, VideoUploadPlan.php: not a format or codec we take
  'video_incomplete',       // keyon/lib/VideoContainer.php: cut off or damaged
  'video_too_complex',      // keyon/lib/VideoContainer.php: over a structural limit
  'video_too_large',        // keyon/lib/VideoUploadPlan.php: over the size limit
  'video_busy',             // keyon/lib/VideoQuota.php: too many open sessions
  'video_quota',            // keyon/lib/VideoQuota.php
  'upload_session_expired', // keyon/lib/VideoUploadService.php
  'incomplete_upload',      // keyon/lib/VideoUploadService.php: complete before the last byte
  'chunk_corrupt',          // keyon/lib/VideoUploadService.php: the chunk did not match its digest; the client resends
  'upload_busy',            // keyon/lib/VideoUploadService.php: a second request on a session mid-chunk
  'upload_state_lost',      // keyon/lib/VideoUploadService.php: the session cannot continue; start again
  'out_of_order',           // keyon/lib/VideoUploadService.php: not the chunk the session expects; the reply carries next_offset
];

/**
 * @param {object} data a response body
 * @returns {string|null} the sentence to show, or null to keep the caller's own
 */
export function refusalMessage(data) {
  if (!data || typeof data !== 'object') return null;
  if (!REFUSAL_CODES.includes(data.code)) return null;
  const text = data.message || data.error;
  return typeof text === 'string' && text.trim() !== '' ? text : null;
}

/**
 * What a refused login code means for the code page.
 *
 * keyon sends `too_many_attempts` when verifying and `too_many_requests` when
 * issuing today; `locked` and `expired` are handled if it sends them.
 *
 * @param {object} data a VerifyLoginCode or Sms6DigitText response body
 * @returns {'locked'|'expired'|'rate_limited'|null} null for a plain mismatch or anything unknown
 */
export function loginCodeRefusalState(data) {
  if (!data || typeof data !== 'object') return null;
  switch (data.code) {
    case 'too_many_attempts':
    case 'locked':
      return 'locked';
    case 'expired':
      return 'expired';
    case 'too_many_requests':
      return 'rate_limited';
    default:
      return null;
  }
}
